import type {ReactNode} from 'react';
import {cx} from './cx';

/**
 * Loading.
 *
 * # There is no loading state anywhere in the design package
 *
 * Not one spinner, skeleton, shimmer or pending state in 485 lines — its menu,
 * options and prices are synchronous arrays, so nothing ever waits. Every screen
 * that fetches needs one anyway, so this is extrapolated from what the package
 * *does* draw and from what already ships:
 *
 *   * The same grey and the same bar shape as the pre-bundle skeleton in
 *     `index.html`. A second loading look on the same page reads as two
 *     different pages loading, and the guest sees `index.html`'s first.
 *   * **Static, never shimmering.** An animation costs frames on the phone least
 *     able to spare them and buys nothing, and `prefers-reduced-motion` would
 *     kill it for some guests anyway — a state that exists for half the audience
 *     is a state nobody designed.
 *   * At the geometry of the real content, so nothing reflows on arrival.
 *   * Never green (nothing is verified) and never red (nothing is the one
 *     action).
 *
 * # Announce it once, at the region, not per bar
 *
 * Put `role="status"` and `aria-busy` on the container that will hold the real
 * content, with a localised label. Thirty announcing bars is a screen reader
 * reading static.
 *
 * ```tsx
 * <div role="status" aria-busy="true" aria-label={t('q3.loading')}>
 *   {[0, 1, 2, 3].map((i) => (
 *     <SkeletonRow key={i}>
 *       <SkeletonBar />
 *       <SkeletonBar width="64px" />
 *     </SkeletonRow>
 *   ))}
 * </div>
 * ```
 */
export function SkeletonBar({
  width,
  height,
  className,
}: {
  /** Any CSS length or percentage. Defaults to filling the row. */
  width?: string;
  /** Defaults to one line of body text. */
  height?: string;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={cx('pk-skeleton', className)}
      style={{width: width ?? '100%', height, flex: width ? '0 0 auto' : '1 1 auto'}}
    />
  );
}

/** A row of bars with the hairline under it — the shape of a menu row. */
export function SkeletonRow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div aria-hidden="true" className={cx('pk-skeleton-row', className)}>
      {children}
    </div>
  );
}
